import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import ScrollToTop from './components/ScrollToTop';
import BackExitGuard from './components/BackExitGuard';
import { AuthProvider } from './context/AuthContext';
import { NotificationBanner } from './components/NotificationBanner';
import RootRedirect from './components/RootRedirect';

// Import components (we'll create these next)
import HomePage from './pages/HomePage';
import SearchResults from './pages/SearchResults';
import ProductDetails from './pages/ProductDetails';
import MeatProductPage from './pages/MeatProductPage';
import BreederProfile from './pages/BreederProfile';
import ButcherProfile from './pages/ButcherProfile';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import BreedersList from './pages/BreedersList';
import AuctionsList from './pages/AuctionsList';
import AuctionDetail from './pages/AuctionDetail';
import WishlistPage from './pages/WishlistPage';
import DeliveryVerify from './pages/DeliveryVerify';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Admin Dashboard
import AdminDashboard from './pages/admin/AdminDashboard';

// Vendor Dashboards
import FarmerDashboard from './pages/farmer/FarmerDashboard';
import ButcherDashboard from './pages/butcher/ButcherDashboard';

// Customer Dashboard
import CustomerDashboard from './pages/customer/CustomerDashboard';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  if (!user.token) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <BackExitGuard />
        <NotificationBanner />
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<RootRedirect><HomePage /></RootRedirect>} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/product/:id" element={<ProductDetails />} />
            <Route path="/meat-product/:id" element={<MeatProductPage />} /> 
            <Route path="/breeder/:id" element={<BreederProfile />} />
            <Route path="/butcher/:id" element={<ButcherProfile />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/breeders" element={<BreedersList />} />
            <Route path="/auctions" element={<AuctionsList />} />
            <Route path="/auction/:id" element={<AuctionDetail />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/delivery/verify" element={<DeliveryVerify />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            
            {/* Admin Routes */}
            <Route 
              path="/admin/*" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Farmer/Cattle Owner Routes */}
            <Route 
              path="/farmer/*" 
              element={
                <ProtectedRoute allowedRoles={['farmer']}>
                  <FarmerDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Butcher/Shop Owner Routes */}
            <Route 
              path="/butcher/*" 
              element={
                <ProtectedRoute allowedRoles={['butcher']}>
                  <ButcherDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Customer Routes */}
            <Route 
              path="/customer/*" 
              element={
                <ProtectedRoute allowedRoles={['customer']}>
                  <CustomerDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* 404 & Unauthorized */}
            <Route path="/unauthorized" element={<div className="p-8 text-center"><h1 className="text-2xl text-red-600">Unauthorized Access</h1></div>} />
            <Route path="*" element={<div className="p-8 text-center"><h1 className="text-2xl">404 - Page Not Found</h1></div>} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
